import { Alert, Badge, Button, DataTable, EmptyState, Field, FormGrid, Input, Modal, NumberInput, PageHeader, Pagination, SearchInput, Select, toast, useDebouncedValue } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Copy, Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, ApiError, dt, errorText, http, STATUS, useCan, useSession } from "../lib/api";
import { useCountdown } from "../lib/use-countdown";

type Row = { id: string; name: string; country: string; plan: string | null; planName: string | null; status: string | null; periodEnd: string | null; addons: string[]; branches: number; users: number; createdAt: string };
type Plan = { id: string; code: string; name: string };

/** Подтверждение email владельца: код из 6 цифр уходит клиенту, он называет его менеджеру → пропуск на создание. */
function EmailStep({ onVerified }: { onVerified: (email: string, token: string) => void }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const resend = useCountdown();
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await http.post<{ resendAfterSec: number }>("/companies/email-code", { email });
      resend.start(r.resendAfterSec);
      setSent(true);
      setCode("");
      toast.success(`Код отправлен на ${email.trim().toLowerCase()}`);
    } catch (e) {
      // Код недавно уже отправлен — ждём отсчёт и вводим его
      const wait = e instanceof ApiError && e.code === "RATE_LIMITED" ? e.details?.retryAfterSec : undefined;
      if (typeof wait === "number" && wait <= 60) {
        resend.start(wait);
        setSent(true);
      } else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await http.post<{ email: string; emailToken: string }>("/companies/email-verify", { email, code });
      onVerified(r.email, r.emailToken);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); void (sent ? verify() : send()); }} noValidate className="flex flex-col gap-5">
      <Alert tone="info">Сначала подтвердим email владельца: на него придёт код из 6 цифр — попросите клиента назвать его. Потом — остальные данные.</Alert>
      <Field label="Email владельца" required error={!sent ? error ?? undefined : undefined}>
        <Input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setSent(false); setError(null); }} autoFocus autoComplete="off" />
      </Field>
      {sent && (
        <Field label="Код из письма клиенту" required error={error ?? undefined} hint={!error ? "Код действует 10 минут. Не пришло — пусть клиент проверит «Спам» и «Промоакции»." : undefined}
          labelAside={<Button variant="link" size="sm" loading={busy && !code} disabled={resend.left > 0} onClick={() => void send()}>{resend.left > 0 ? `Ещё раз через ${resend.left} с` : "Отправить ещё раз"}</Button>}>
          <Input value={code} onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(null); }} inputMode="numeric" autoComplete="one-time-code" placeholder="123456" autoFocus />
        </Field>
      )}
      {sent
        ? <Button type="submit" loading={busy && code.length === 6} disabled={code.length !== 6}>Подтвердить email</Button>
        : <Button type="submit" loading={busy} disabled={!email.trim()}>Отправить код</Button>}
    </form>
  );
}

/** Ручное создание клиента (US-PA-03): email владельца подтверждается кодом, затем данные; владелец получает ссылку установки пароля на 72 ч. */
function CreateModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => http.get<Plan[]>("/plans") });
  const can = useCan();
  const me = useSession()!.user;
  // Назначать менеджера может владелец/администратор; менеджер по продажам закрепляет клиента за собой
  const assign = can("companies.assign_manager");
  const managers = useQuery({ queryKey: ["staff-options", "managers"], queryFn: () => http.get<{ id: string; name: string }[]>("/staff/options", { managers: 1 }), enabled: assign });
  const [verified, setVerified] = useState<{ email: string; token: string } | null>(null);
  const [f, setF] = useState({ companyName: "", ownerName: "", planCode: "business", trialDays: 14 as number | null, managerId: "none" });
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState<string | null>(null);
  const erp = import.meta.env.VITE_ERP_URL ?? "http://localhost:5173";
  const submit = async () => {
    if (!verified) return;
    setBusy(true);
    try {
      const r = await http.post<{ companyId: string; inviteUrl: string }>("/companies", {
        ...f, ownerEmail: verified.email, emailToken: verified.token, trialDays: f.trialDays ?? 0, managerId: assign && f.managerId !== "none" ? f.managerId : undefined,
      });
      setInvite(`${erp}${r.inviteUrl}`);
      void qc.invalidateQueries({ queryKey: ["companies"] });
    } catch (e) {
      // Пропуск истёк (1 ч) — подтвердить email заново
      if (e instanceof ApiError && e.fields.some((x) => x.path === "ownerEmail")) setVerified(null);
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Новый клиент" description={verified ? "Шаг 2 из 2: данные клиента" : "Шаг 1 из 2: подтверждение email"} size="lg"
      footer={invite ? <Button onClick={onClose}>Готово</Button> : verified ? <><Button onClick={() => void submit()} loading={busy} disabled={!f.companyName || !f.ownerName}>Создать</Button><Button variant="outline" onClick={onClose}>Отмена</Button></> : <Button variant="outline" onClick={onClose}>Отмена</Button>}>
      {invite ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm">Компания создана. Отправьте владельцу ссылку для установки пароля (действует 72 часа):</p>
          <div className="flex gap-2"><Input readOnly value={invite} aria-label="Ссылка приглашения" /><Button variant="outline" leftIcon={<Copy />} onClick={() => void navigator.clipboard.writeText(invite).then(() => toast.success("Скопировано"))}>Копировать</Button></div>
        </div>
      ) : !verified ? (
        <EmailStep onVerified={(email, token) => setVerified({ email, token })} />
      ) : (
        <FormGrid cols={2}>
          <Field label="Email владельца" className="md:col-span-2" labelAside={<Button variant="link" size="sm" onClick={() => setVerified(null)}>Изменить</Button>}>
            <Input readOnly value={verified.email} rightIcon={<Badge tone="success">подтверждён</Badge>} />
          </Field>
          <Field label="Компания" required><Input value={f.companyName} onChange={(e) => setF({ ...f, companyName: e.target.value })} autoFocus /></Field>
          <Field label="Владелец" required><Input value={f.ownerName} onChange={(e) => setF({ ...f, ownerName: e.target.value })} /></Field>
          <Field label="Тариф"><Select value={f.planCode} onValueChange={(v) => setF({ ...f, planCode: v })} options={(plans.data ?? []).map((p) => ({ value: p.code, label: p.name }))} /></Field>
          <Field label="Бесплатный пробный период, дней" hint="0 — без пробного периода, сразу платная подписка"><NumberInput stepper value={f.trialDays} onValueChange={(v) => setF({ ...f, trialDays: v })} min={0} max={90} /></Field>
          {assign
            ? <Field label="Менеджер" hint="За кем закреплён клиент: его оплаты попадут в «Финансы сотрудников»">
                <Select value={f.managerId} onValueChange={(v) => setF({ ...f, managerId: v })} options={[{ value: "none", label: "Не закреплён" }, ...(managers.data ?? []).map((u) => ({ value: u.id, label: u.name }))]} />
              </Field>
            : me.role === "sales" && <Field label="Менеджер" hint="Клиент будет закреплён за вами"><Input readOnly value={me.name} /></Field>}
        </FormGrid>
      )}
    </Modal>
  );
}

/** Клиенты платформы: поиск по названию/ИНН/email/телефону, фильтры по статусу и тарифу. */
export default function CompaniesPage() {
  const can = useCan();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const dq = useDebouncedValue(q, 300);
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const list = useQuery({
    queryKey: ["companies", dq, status, page],
    queryFn: () => api<{ items: Row[]; meta: { total: number } }>("GET", "/companies", undefined, { withMeta: true, query: { q: dq || undefined, status: status === "all" ? undefined : status, page, pageSize: 30 } }),
  });
  return (
    <>
      <PageHeader title="Компании" description="Клиенты платформы, их тарифы и статусы подписок." actions={can("companies.create") && <Button leftIcon={<Plus />} onClick={() => setCreating(true)}>Новый клиент</Button>} />
      <div className="flex flex-col gap-3 sm:flex-row">
        <SearchInput value={q} onValueChange={(v) => { setQ(v); setPage(1); }} placeholder="Название, ИНН, email, телефон" className="sm:max-w-sm" />
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }} className="sm:w-56" options={[{ value: "all", label: "Все статусы" }, ...Object.entries(STATUS).map(([k, v]) => ({ value: k, label: v.label }))]} />
      </div>
      <DataTable rows={list.data?.items} loading={list.isPending} rowKey={(r) => r.id} onRowClick={(r) => navigate(`/companies/${r.id}`)} empty={<EmptyState icon={<Building2 />} title="Компаний нет" />}
        columns={[
          { key: "name", header: "Компания", cell: (r) => <span className="font-medium">{r.name}</span>, mobile: "title" },
          { key: "plan", header: "Тариф", cell: (r) => r.planName ?? "—", mobile: "subtitle" },
          { key: "status", header: "Статус", cell: (r) => (r.status ? <Badge tone={STATUS[r.status]?.tone ?? "neutral"} dot>{STATUS[r.status]?.label ?? r.status}</Badge> : "—") },
          { key: "addons", header: "Дополнений", cell: (r) => (r.addons.length ? <span className="text-xs text-muted">{r.addons.length}</span> : "—") },
          { key: "usage", header: "Филиалов / пользователей", align: "end", cell: (r) => <span className="tabular-nums">{r.branches} / {r.users}</span> },
          { key: "end", header: "Оплачено до", cell: (r) => dt(r.periodEnd) },
        ]} />
      {list.data && <Pagination page={page} pageSize={30} total={list.data.meta.total} onPageChange={setPage} />}
      {creating && <CreateModal onClose={() => setCreating(false)} />}
    </>
  );
}
